import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Check,
  Zap,
  ArrowUp,
  ArrowDown,
  FileText,
  Download,
  Plus,
  ArrowUpCircle,
  ArrowDownCircle,
  Wallet as WalletIcon,
  CreditCard,
} from 'lucide-react'
import { loadStripe, type Stripe, type StripeCardElement } from '@stripe/stripe-js'
import { billingApi, type BillingPlan } from '@/api/billing'
import { usePlanLimits } from '@/hooks/usePlanLimits'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDate, formatCredits } from '@/lib/utils'

function formatPrice(cents: number, interval: string): string {
  if (cents === 0) return 'Free'
  return `$${(cents / 100).toFixed(0)}/${interval === 'year' ? 'yr' : 'mo'}`
}

// ---------------------------------------------------------------------------
// Subscription Tab
// ---------------------------------------------------------------------------
function getAnnualPrice(plan: BillingPlan): number | null {
  if (plan.annual_price_cents != null) return plan.annual_price_cents
  if (plan.price_cents === 0) return null
  if (plan.annual_free_months > 0) {
    return plan.price_cents * (12 - plan.annual_free_months)
  }
  if (plan.annual_discount_percent > 0) {
    return Math.round(plan.price_cents * 12 * (1 - plan.annual_discount_percent / 100))
  }
  return null
}

function getAnnualSavingsLabel(plan: BillingPlan): string | null {
  if (plan.price_cents === 0) return null
  if (plan.annual_free_months > 0) {
    return `${plan.annual_free_months} month${plan.annual_free_months > 1 ? 's' : ''} free`
  }
  if (plan.annual_discount_percent > 0) {
    return `${plan.annual_discount_percent}% off`
  }
  return null
}

function SubscriptionTab() {
  const queryClient = useQueryClient()
  const { data: plans = [] } = useQuery({
    queryKey: ['billing-plans'],
    queryFn: billingApi.listPlans,
  })
  const { subscription } = usePlanLimits()
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month')

  const [confirmDialog, setConfirmDialog] = useState<{
    plan: BillingPlan
    isUpgrade: boolean
  } | null>(null)

  const changePlanMutation = useMutation({
    mutationFn: ({ planId, interval }: { planId: string; interval: string }) =>
      billingApi.changePlan(planId, interval),
    onSuccess: (result) => {
      setConfirmDialog(null)
      if (result.checkout_url) {
        window.location.href = result.checkout_url
      } else {
        queryClient.invalidateQueries({ queryKey: ['subscription'] })
        queryClient.invalidateQueries({ queryKey: ['billing-plans'] })
      }
    },
  })

  const portalMutation = useMutation({
    mutationFn: billingApi.getBillingPortal,
    onSuccess: ({ url }) => {
      window.location.href = url
    },
  })

  const handlePlanAction = (plan: BillingPlan) => {
    const currentPrice = plans.find((p) => p.id === subscription?.plan_id)?.price_cents ?? 0
    const isUpgrade = plan.price_cents > currentPrice
    setConfirmDialog({ plan, isUpgrade })
  }

  return (
    <div className="space-y-6">
      {subscription && (
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <div>
            <p className="font-medium">
              Current subscription: {subscription.plan_name}
            </p>
            <p className="text-sm text-muted-foreground capitalize">
              Status: {subscription.status}
            </p>
            {subscription.current_period_end && (
              <p className="text-sm text-muted-foreground">
                Current period ends: {formatDate(subscription.current_period_end)}
              </p>
            )}
            {(() => {
              const currentPlan = plans.find((p) => p.id === subscription.plan_id)
              if (
                currentPlan &&
                currentPlan.price_cents > 0 &&
                subscription.current_period_end &&
                subscription.status === 'active'
              ) {
                return (
                  <p className="text-sm font-medium mt-1">
                    Next charge: ${(currentPlan.price_cents / 100).toFixed(2)} on{' '}
                    {formatDate(subscription.current_period_end)}
                  </p>
                )
              }
              return null
            })()}
          </div>
          {subscription.status === 'active' &&
            subscription.plan_id !==
              plans.find((p) => p.slug === 'free')?.id && (
              <Button
                variant="outline"
                onClick={() => portalMutation.mutate()}
                disabled={portalMutation.isPending}
              >
                Manage Subscription
              </Button>
            )}
        </div>
      )}

      {/* Monthly / Annual toggle */}
      {plans.some((p) => getAnnualPrice(p) !== null) && (
        <div className="flex items-center justify-center gap-2">
          <button
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              billingInterval === 'month'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
            onClick={() => setBillingInterval('month')}
          >
            Monthly
          </button>
          <button
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              billingInterval === 'year'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
            onClick={() => setBillingInterval('year')}
          >
            Annual
          </button>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        {plans.map((plan) => {
          const isCurrent = subscription?.plan_id === plan.id
          const currentPrice =
            plans.find((p) => p.id === subscription?.plan_id)?.price_cents ?? 0
          const isUpgrade = plan.price_cents > currentPrice
          const isDowngrade = plan.price_cents < currentPrice && plan.price_cents > 0

          const annualPrice = getAnnualPrice(plan)
          const savingsLabel = getAnnualSavingsLabel(plan)
          const showAnnual = billingInterval === 'year' && annualPrice !== null

          return (
            <Card
              key={plan.id}
              className={isCurrent ? 'border-primary shadow-md' : ''}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{plan.name}</CardTitle>
                  {isCurrent && <Badge>Current</Badge>}
                </div>
                <CardDescription>{plan.description}</CardDescription>
                <div className="mt-2">
                  <div className="text-3xl font-bold">
                    {showAnnual
                      ? formatPrice(annualPrice, 'year')
                      : formatPrice(plan.price_cents, plan.billing_interval)}
                  </div>
                  {showAnnual && savingsLabel && (
                    <p className="text-sm text-green-600 font-medium mt-1">
                      {savingsLabel}
                    </p>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  {plan.max_projects && (
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{plan.max_projects} projects</span>
                    </div>
                  )}
                  {plan.max_documents_per_project && (
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{plan.max_documents_per_project} docs/project</span>
                    </div>
                  )}
                  {plan.max_fields_per_project && (
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{plan.max_fields_per_project} fields/project</span>
                    </div>
                  )}
                  {plan.max_users && (
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{plan.max_users} team members</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span>
                      {Number(plan.credits_per_page_batch_mode).toFixed(1)} cr/page (batch)
                    </span>
                  </div>
                  {plan.monthly_wallet_credits > 0 && (
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{plan.monthly_wallet_credits.toLocaleString()} credits / mo (~{plan.monthly_wallet_credits.toLocaleString()} pages)</span>
                    </div>
                  )}
                  {plan.chat_enabled && (
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-yellow-500" />
                      <span>AI Chat enabled</span>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter>
                {isCurrent ? (
                  <Button variant="outline" className="w-full" disabled>
                    Current Plan
                  </Button>
                ) : plan.price_cents === 0 && currentPrice === 0 ? (
                  <Button variant="outline" className="w-full" disabled>
                    Free Tier
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    variant={isDowngrade ? 'outline' : 'default'}
                    onClick={() => handlePlanAction(plan)}
                    disabled={changePlanMutation.isPending}
                  >
                    {isUpgrade && (
                      <ArrowUp className="h-4 w-4 mr-1" />
                    )}
                    {isDowngrade && (
                      <ArrowDown className="h-4 w-4 mr-1" />
                    )}
                    {isUpgrade
                      ? 'Upgrade'
                      : isDowngrade
                        ? 'Downgrade'
                        : 'Switch Plan'}
                  </Button>
                )}
              </CardFooter>
            </Card>
          )
        })}
      </div>

      {/* Confirm Dialog */}
      <Dialog
        open={!!confirmDialog}
        onOpenChange={(open) => !open && setConfirmDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDialog?.isUpgrade ? 'Upgrade' : 'Downgrade'} to{' '}
              {confirmDialog?.plan.name}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog?.isUpgrade
                ? 'Your plan will be upgraded immediately. You will be charged a prorated amount for the remainder of your current billing period.'
                : 'Your plan will be changed at the end of your current billing period. You will continue to have access to your current plan features until then.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex justify-between items-center p-3 bg-muted rounded-md">
              <span className="text-sm font-medium">New plan price</span>
              <span className="font-bold">
                {confirmDialog
                  ? (() => {
                      const annualP = getAnnualPrice(confirmDialog.plan)
                      if (billingInterval === 'year' && annualP !== null) {
                        return formatPrice(annualP, 'year')
                      }
                      return formatPrice(
                        confirmDialog.plan.price_cents,
                        confirmDialog.plan.billing_interval
                      )
                    })()
                  : ''}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog(null)}
              disabled={changePlanMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                confirmDialog &&
                changePlanMutation.mutate({
                  planId: confirmDialog.plan.id,
                  interval: billingInterval,
                })
              }
              disabled={changePlanMutation.isPending}
            >
              {changePlanMutation.isPending
                ? 'Processing...'
                : confirmDialog?.isUpgrade
                  ? 'Confirm Upgrade'
                  : 'Confirm Downgrade'}
            </Button>
          </DialogFooter>
          {changePlanMutation.isError && (
            <p className="text-sm text-red-500 mt-2">
              {(changePlanMutation.error as Error)?.message ||
                'Something went wrong. Please try again.'}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Invoices Tab
// ---------------------------------------------------------------------------
function InvoicesTab() {
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: billingApi.listInvoices,
  })

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading invoices...</div>
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No invoices yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-3 text-sm font-medium">Date</th>
              <th className="text-left p-3 text-sm font-medium">Description</th>
              <th className="text-left p-3 text-sm font-medium">Amount</th>
              <th className="text-left p-3 text-sm font-medium">Status</th>
              <th className="text-right p-3 text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="border-b last:border-0">
                <td className="p-3 text-sm">{formatDate(invoice.created_at)}</td>
                <td className="p-3 text-sm">
                  {invoice.description || 'Invoice'}
                </td>
                <td className="p-3 text-sm font-medium">
                  ${(invoice.amount_cents / 100).toFixed(2)}
                </td>
                <td className="p-3">
                  <Badge
                    variant={
                      invoice.status === 'paid'
                        ? 'default'
                        : invoice.status === 'pending'
                          ? 'secondary'
                          : 'destructive'
                    }
                  >
                    {invoice.status}
                  </Badge>
                </td>
                <td className="p-3 text-right">
                  {invoice.pdf_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                    >
                      <a
                        href={invoice.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </a>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Wallet Tab (with Stripe.js card payment)
// ---------------------------------------------------------------------------
function WalletTab() {
  const queryClient = useQueryClient()
  const [topupAmount, setTopupAmount] = useState('')
  const [showTopup, setShowTopup] = useState(false)
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null)
  const [cardElement, setCardElement] = useState<StripeCardElement | null>(null)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const cardContainerRef = useRef<HTMLDivElement>(null)

  const { data: balance } = useQuery({
    queryKey: ['wallet-balance'],
    queryFn: billingApi.getWalletBalance,
  })

  const { data: transactions = [] } = useQuery({
    queryKey: ['wallet-transactions'],
    queryFn: billingApi.listTransactions,
  })

  const { data: billingConfig } = useQuery({
    queryKey: ['billing-config'],
    queryFn: billingApi.getBillingConfig,
    staleTime: Infinity,
  })

  // Initialize Stripe when config is available
  useEffect(() => {
    if (billingConfig?.publishable_key && !stripeInstance) {
      loadStripe(billingConfig.publishable_key).then((stripe) => {
        if (stripe) setStripeInstance(stripe)
      })
    }
  }, [billingConfig?.publishable_key, stripeInstance])

  // Mount Stripe card element when showing topup form
  const mountCardElement = useCallback(() => {
    if (!stripeInstance || !cardContainerRef.current) return
    // Clear any existing elements
    cardContainerRef.current.innerHTML = ''
    const elements = stripeInstance.elements()
    const card = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          color: '#1a1a1a',
          '::placeholder': { color: '#a3a3a3' },
        },
      },
    })
    card.mount(cardContainerRef.current)
    setCardElement(card)
  }, [stripeInstance])

  useEffect(() => {
    if (showTopup && stripeInstance && cardContainerRef.current) {
      // Small delay to allow DOM to render before mounting
      const timer = setTimeout(mountCardElement, 50)
      return () => clearTimeout(timer)
    } else {
      setCardElement(null)
    }
  }, [showTopup, stripeInstance, mountCardElement])

  const topupMutation = useMutation({
    mutationFn: (amountCents: number) => billingApi.topupWallet(amountCents),
  })

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount)
    if (isNaN(amount) || amount < 1) return

    setPaymentError(null)
    setPaymentProcessing(true)

    try {
      // Step 1: Create the PaymentIntent on the server
      const { client_secret } = await topupMutation.mutateAsync(
        Math.round(amount * 100)
      )

      // Step 2: Confirm payment with Stripe.js
      if (!stripeInstance) {
        setPaymentError(
          'Stripe is not loaded. Please refresh the page and try again.'
        )
        setPaymentProcessing(false)
        return
      }

      if (!cardElement) {
        setPaymentError('Card element not ready. Please try again.')
        setPaymentProcessing(false)
        return
      }

      const { error, paymentIntent } =
        await stripeInstance.confirmCardPayment(client_secret, {
          payment_method: { card: cardElement },
        })

      if (error) {
        setPaymentError(error.message || 'Payment failed. Please try again.')
        setPaymentProcessing(false)
        return
      }

      if (paymentIntent?.status === 'succeeded') {
        setPaymentSuccess(true)
        setPaymentProcessing(false)
        setTopupAmount('')

        // Wait briefly for webhook to process, then refetch
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['wallet-balance'] })
          queryClient.invalidateQueries({
            queryKey: ['wallet-transactions'],
          })
        }, 2000)

        // Hide success after 3s
        setTimeout(() => {
          setPaymentSuccess(false)
          setShowTopup(false)
        }, 3000)
      }
    } catch {
      setPaymentError('Failed to create payment. Please try again.')
      setPaymentProcessing(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Balance</CardTitle>
            <WalletIcon className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {formatCredits(balance?.balance_credits ?? 0)} credits
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              ~{(balance?.balance_credits ?? 0).toLocaleString()} pages at standard model
            </p>
            <Button
              className="mt-4"
              size="sm"
              onClick={() => {
                setShowTopup(true)
                setPaymentError(null)
                setPaymentSuccess(false)
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Top Up
            </Button>
          </CardContent>
        </Card>

        {showTopup && (
          <Card className="border-primary">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Add Funds
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {paymentSuccess ? (
                <div className="text-center py-4">
                  <Check className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="font-medium text-green-700">
                    Payment successful!
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Funds will be added to your wallet shortly.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Amount (USD)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        $
                      </span>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="10"
                        value={topupAmount}
                        onChange={(e) => setTopupAmount(e.target.value)}
                        className="pl-7"
                        disabled={paymentProcessing}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Will be added to your wallet balance
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Card Details</Label>
                    <div
                      ref={cardContainerRef}
                      className="p-3 border rounded-md bg-white min-h-[44px]"
                    />
                    {!stripeInstance && (
                      <p className="text-xs text-muted-foreground">
                        Loading payment form...
                      </p>
                    )}
                  </div>

                  {paymentError && (
                    <p className="text-sm text-red-500">{paymentError}</p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={handleTopup}
                      disabled={
                        paymentProcessing ||
                        !cardElement ||
                        !topupAmount ||
                        parseFloat(topupAmount) < 1
                      }
                      size="sm"
                    >
                      {paymentProcessing ? 'Processing...' : 'Pay with Card'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowTopup(false)
                        setPaymentError(null)
                      }}
                      disabled={paymentProcessing}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-4">Transaction History</h2>
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No transactions yet
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((txn) => (
              <Card key={txn.id}>
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    {txn.type === 'topup' || txn.type === 'refund' ? (
                      <ArrowUpCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <ArrowDownCircle className="h-5 w-5 text-red-500" />
                    )}
                    <div>
                      <p className="text-sm font-medium">
                        {txn.description || txn.type}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(txn.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`font-medium ${txn.type === 'topup' || txn.type === 'refund' ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {txn.type === 'topup' || txn.type === 'refund'
                        ? '+'
                        : '-'}
                      {formatCredits(txn.amount_credits)} credits
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Balance: {formatCredits(txn.balance_after_credits)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Billing Page
// ---------------------------------------------------------------------------
export default function Plans() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Billing</h1>
        <p className="text-muted-foreground">
          Manage your subscription, view invoices, and top up your wallet
        </p>
      </div>

      <Tabs defaultValue="subscription" className="space-y-4">
        <TabsList>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="wallet">Wallet</TabsTrigger>
        </TabsList>

        <TabsContent value="subscription">
          <SubscriptionTab />
        </TabsContent>

        <TabsContent value="invoices">
          <InvoicesTab />
        </TabsContent>

        <TabsContent value="wallet">
          <WalletTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
